import ContactForm from "../components/ContactForm";
import SiteHeader from "../components/SiteHeader";
import { ServiceVisual } from "../components/ServiceVisual";
import TrackedLink from "../components/TrackedLink";
import "../styles/build-migrate.css";
import "../styles/troubleshooting.css";

const capabilities = [
  {
    title: "Visibility across the stack",
    description: "Connect metrics, logs and traces across infrastructure, databases and application requests. Give engineers the evidence to distinguish traffic growth from a slow query, leaking memory or a CPU-heavy code path.",
    icon: "visibility",
  },
  {
    title: "Application & database troubleshooting",
    description: "Investigate recurring outages, slow endpoints, memory leaks, inefficient queries and database contention. Work with your developers to isolate the cause and check that the change addresses it.",
    icon: "investigate",
  },
  {
    title: "Infrastructure & network troubleshooting",
    description: "Trace failures across compute, containers, DNS, routing and dependencies. Separate capacity and configuration problems from application behaviour before adding resources or changing the architecture.",
    icon: "infrastructure",
  },
  {
    title: "Reviewable infrastructure changes",
    description: "Bring existing AWS resources under Terraform and version control in stages. Make configuration changes and drift visible, with reviewable plans and checks before touching live resources.",
    icon: "changes",
  },
] as const;

// Evidence from Ebrahim's CV; the CPU investigation also reflects his project account.
const projects = [
  {
    title: "Database performance",
    result: "35–40%",
    resultLabel: "lower database load",
    detail: "I worked with developers to isolate inefficient queries and application behaviour using database telemetry, reducing memory pressure, deadlocks and customer-facing timeouts.",
  },
  {
    title: "Recurring production outages",
    result: "Memory leak identified",
    resultLabel: "through distributed tracing",
    detail: "I built end-to-end observability and used the traces to isolate a long-standing application memory leak behind recurring outages.",
  },
] as const;

const deliverySteps = [
  { title: "Establish the facts", detail: "Map the system, agree the impact and gather a baseline. Add the visibility needed to investigate.", output: "A system map & baseline" },
  { title: "Narrow down the cause", detail: "Test explanations against metrics, logs and traces with your engineers, separating symptoms from the cause.", output: "Evidence & recommended changes" },
  { title: "Resolve & verify", detail: "Make reviewed changes with a rollback plan, alongside your engineers. Check the outcome against the baseline and document what changed.", output: "Validated changes & handover" },
] as const;

function TroubleshootingIcon({ type }: { type: typeof capabilities[number]["icon"] | "map" }) {
  return (
    <svg className="bm-capability-icon" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      {type === "map" && <><rect x="3" y="3" width="9" height="8" rx="2" /><rect x="20" y="3" width="9" height="8" rx="2" /><rect x="11" y="22" width="10" height="8" rx="2" /><path d="M7 11v6h18v-6M16 17v5" /></>}
      {type === "visibility" && <><rect x="3" y="4" width="26" height="22" rx="3" /><path d="M7 17h4l3-6 4 11 3-6h4M11 30h10M16 26v4" /></>}
      {type === "investigate" && <><circle cx="14" cy="14" r="10" /><path d="m21 21 8 8M8 15h4l2-5 3 8 2-3h2" /></>}
      {type === "infrastructure" && <><rect x="4" y="3" width="24" height="8" rx="2" /><rect x="4" y="15" width="24" height="8" rx="2" /><path d="M8 7h1m4 0h10M8 19h1m4 0h10M16 23v6M8 29h16" /></>}
      {type === "changes" && <><circle cx="8" cy="6" r="3" /><circle cx="8" cy="26" r="3" /><circle cx="24" cy="9" r="3" /><path d="M8 9v14m0-7h8a8 8 0 0 0 8-4m-7 11 4 4 8-9" /></>}
    </svg>
  );
}

export default function ProductionServicePage() {
  return (
    <main className="service-main troubleshooting-page">
      <SiteHeader />
      <section className="bm-shell bm-hero" aria-labelledby="troubleshooting-title">
        <div className="bm-hero-copy">
          <h1 id="troubleshooting-title">Production troubleshooting.</h1>
          <p className="bm-intro">
            I help founders and engineering teams investigate slow, unstable or
            undocumented systems. I connect evidence across infrastructure,
            databases and applications, work with your engineers to resolve the
            cause, and leave the system easier to understand and change.
          </p>
          <div className="hero-actions">
            <TrackedLink className="button button-primary" href="#contact" eventName="Contact CTA Click" eventProps={{ location: "troubleshooting-hero" }}>
              Discuss a production issue <span aria-hidden="true">↗</span>
            </TrackedLink>
            <a className="button button-secondary" href="#project-work">See relevant work</a>
          </div>
        </div>
        <div className="bm-hero-aside" aria-hidden="true">
          <div className="bm-illustration"><ServiceVisual type="observability" /></div>
        </div>
      </section>

      <section className="bm-credential-band" aria-label="AWS certifications">
        <div className="bm-shell bm-certifications troubleshooting-certifications">
          <div className="bm-certification">
            <img src="/aws-certified-devops-engineer-professional.png" alt="" width="44" height="44" decoding="async" />
            <p><span>AWS Certified</span><strong>DevOps Engineer – Professional</strong></p>
          </div>
          <div className="bm-certification">
            <img src="/aws-certified-sysops-administrator-associate.png" alt="" width="44" height="44" decoding="async" />
            <p><span>AWS Certified</span><strong>SysOps Administrator – Associate</strong></p>
          </div>
        </div>
      </section>

      <section className="bm-services" aria-labelledby="troubleshooting-scope-title">
        <div className="bm-shell bm-section">
          <h2 id="troubleshooting-scope-title">Where I can help.</h2>
          <article className="bm-architecture">
            <div className="bm-capability-title"><TroubleshootingIcon type="map" /><h3>Understand an unfamiliar system</h3></div>
            <p>Map what is running, how services depend on each other, and what changed—even when documentation is missing or out of date. Give your team a shared picture of the system and where to investigate.</p>
          </article>
          <div className="bm-capabilities">
            {capabilities.map((item) => (
              <article className="bm-capability" key={item.icon}>
                <div className="bm-capability-title"><TroubleshootingIcon type={item.icon} /><h3>{item.title}</h3></div>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
          <details className="bm-tools">
            <summary>Technical experience behind the work</summary>
            <p>OpenTelemetry, CloudWatch, AWS X-Ray, Grafana, RDS Performance Insights, PostgreSQL telemetry, Linux, Docker, ECS, EC2 and Terraform.</p>
            <p>Instrumentation follows the investigation. I use or extend the tools your team already has.</p>
          </details>
        </div>
      </section>

      <section className="bm-work" id="project-work" aria-labelledby="troubleshooting-work-title">
        <div className="bm-shell bm-section">
          <h2 id="troubleshooting-work-title" className="bm-lower-heading">Results from client work.</h2>
          <article className="bm-proof-feature troubleshooting-feature">
            <div className="bm-proof-story">
              <h3>High CPU. Normal traffic.</h3>
              <p>I compared request volume with application telemetry and narrowed the CPU pressure to a specific endpoint.</p>
            </div>
            <div className="troubleshooting-finding">
              <span>Cause identified</span>
              <strong>A CPU-consuming software loop.</strong>
              <p>The developers had a concrete cause to address.</p>
            </div>
          </article>
          <div className="bm-proof-grid">
            {projects.map((project) => (
              <article className="bm-proof-card" key={project.title}>
                <h3>{project.title}</h3>
                <div className="bm-proof-result"><strong>{project.result}</strong><span>{project.resultLabel}</span></div>
                <p>{project.detail}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bm-shell bm-section bm-delivery" aria-labelledby="troubleshooting-delivery-title">
        <h2 id="troubleshooting-delivery-title" className="bm-lower-heading">How I deliver.</h2>
        <ol className="bm-delivery-list" role="list">
          {deliverySteps.map((step, index) => (
            <li key={step.title}>
              <div className="bm-delivery-title"><span aria-hidden="true">0{index + 1}</span><h3>{step.title}</h3></div>
              <p>{step.detail}</p>
              <div className="bm-delivery-output"><span>You get</span><strong>{step.output}</strong></div>
            </li>
          ))}
        </ol>
      </section>

      <section className="bm-contact-section" id="contact" aria-labelledby="troubleshooting-contact-title">
        <div className="bm-shell bm-contact-panel">
          <div className="bm-contact-copy">
            <p className="section-kicker">Let’s talk</p>
            <h2 id="troubleshooting-contact-title">What’s happening in production?</h2>
            <p>Tell me what is slow, unreliable or difficult to understand, and what you’ve already tried. We can discuss whether I can help.</p>
            <div className="bm-engagement-note">
              <p><strong>Your work isn’t passed down to another subcontractor.</strong></p>
              <p>I’m available for short projects, fixed-scope work and longer-term engagements.</p>
            </div>
            <p className="bm-personal-reply">I’ll reply personally.</p>
          </div>
          <ContactForm
            variant="compact"
            contextPrompt="A few details to see if I can help."
            messageLabel="What are you seeing?"
            messagePlaceholder="What is happening, who is affected, and what have you already tried? A short summary is enough—please don’t include credentials or sensitive data."
            subjectLabel="Production troubleshooting enquiry"
          />
        </div>
      </section>

      <footer className="service-footer">
        <span>© 2026 Ebi Consulting Oy · Y-tunnus 3597750-8</span>
        <a href="/">Back to the main site ↑</a>
      </footer>
    </main>
  );
}
